import { EventEmitter, Injectable } from '@angular/core';
import { FeatureCollection, Geometry } from 'geojson';
import * as mapboxgl from 'mapbox-gl';
import { MapService } from 'src/app/shared/components/map/map.service';
import { ResultLayer } from '../model/result-layers';
import { ResultMapLayerCreationService } from './result-map-layer-creation.service';
import { ResultMapPopupService } from './result-map-popup.service';
import { ResultMapSquareLayerService } from './result-map-square-layer.service';
import { ResultMapService } from './result-map.service';
import { ResultMapFilterService } from './result-map-filter.service';
import { take } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ResultMapLayerService {

  private static SOURCE_NAME = 'mitra-result';

  constructor(
    private resultMapService: ResultMapService,
    private mapService: MapService,
    private creationLayer: ResultMapLayerCreationService,
    private resultMapSquareLayerService: ResultMapSquareLayerService,
    private resultMapPopupService: ResultMapPopupService,
    private resultMapFilterService: ResultMapFilterService
  ) { }

  load(projectId: number, simulationId: any): Observable<void> {
    const emmiter = new EventEmitter<void>();
    this.resultMapService.get(projectId, simulationId)
      .pipe(take(1))
      .subscribe(collection => {
        this.populateMap(collection);
        this.resultMapFilterService.setCollection(collection);
        emmiter.next();
      });

    this.resultMapSquareLayerService.load(projectId, simulationId);
    return emmiter.asObservable();
  }

  clear() {
    this.resultMapSquareLayerService.clean();
    this.mapService.map$.subscribe(m => {
      const source = this.getSource(m);
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: []
        });
      }
    });
  }

  setDataSource(m: mapboxgl.Map, collection: FeatureCollection<Geometry, { [name: string]: any; }>) {
    this.createDataSource(m, collection);
  }

  private getSource(m: mapboxgl.Map) {
    return m.getSource(ResultMapLayerService.SOURCE_NAME) as mapboxgl.GeoJSONSource;
  }

  private populateMap(collection: FeatureCollection<Geometry, { [name: string]: any; }>) {
    this.mapService.map$.subscribe(m => {
      const created = this.createDataSource(m, collection);
      if (created) {
        this.createLayers(m);
      }
    });
  }

  private createDataSource(mapbox: mapboxgl.Map, collection: FeatureCollection<Geometry, { [name: string]: any; }>): boolean {
    const source = this.getSource(mapbox);
    if (source) {
      source.setData(collection);
      return false;
    }
    mapbox.addSource(
      ResultMapLayerService.SOURCE_NAME,
      {
        type: 'geojson',
        data: collection
      });

    return true;
  }

  private createLayers(mapbox: mapboxgl.Map) {
    this.creationLayer.create(mapbox, ResultMapLayerService.SOURCE_NAME);
    const layers = Object.values(ResultLayer);
    this.resultMapPopupService.listen(mapbox, layers);
  }
}
